import { useEffect } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AuthUser, UserProfile } from '@portal-leo/shared';
import { trpc, setAuthToken, removeAuthToken } from '@/lib/trpc';

interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  setUser: (user: AuthUser | null) => void;
  logout: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      isAuthenticated: false,
      setUser: (user) => {
        // Compat: handle old localStorage format with `perfil` instead of `permissoes`
        if (user && !(user as any).permissoes && (user as any).perfil) {
          user = { ...user, permissoes: [(user as any).perfil as UserProfile] };
        }
        set({ user, isAuthenticated: !!user });
      },
      logout: () => {
        removeAuthToken();
        set({ user: null, isAuthenticated: false });
      },
    }),
    {
      name: 'auth-storage',
      onRehydrateStorage: () => (state) => {
        // Compat: migrate old localStorage format with `perfil` instead of `permissoes`
        if (state?.user && !(state.user as any).permissoes && (state.user as any).perfil) {
          state.setUser({ ...state.user, permissoes: [(state.user as any).perfil as UserProfile] });
        }
      },
    }
  )
);

export function useAuth() {
  const { user, isAuthenticated, setUser, logout } = useAuthStore();
  const utils = trpc.useUtils();

  const loginMutation = trpc.auth.login.useMutation({
    onSuccess: (data) => {
      setAuthToken(data.token);
      setUser(data.user);
      utils.invalidate();
    },
  });

  const logoutMutation = trpc.auth.logout.useMutation({
    onSuccess: () => {
      logout();
      utils.invalidate();
    },
  });

  const meQuery = trpc.auth.me.useQuery(undefined, {
    enabled: isAuthenticated,
    retry: false,
  });

  useEffect(() => {
    if (meQuery.error) {
      logout();
    }
  }, [meQuery.error, logout]);

  const hasPermission = (...perms: UserProfile[]) => {
    if (!user || !user.permissoes) return false;
    return perms.some((p) => user.permissoes.includes(p));
  };

  return {
    user,
    isAuthenticated,
    isLoading: loginMutation.isPending,
    login: loginMutation.mutateAsync,
    logout: logoutMutation.mutate,
    meQuery,
    hasPermission,
  };
}
